import PropTypes from "prop-types";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatError } from "helpers/formatError";
import { useRuvixxFlags } from "providers/RuvixxFlagsProvider";
import useSwoogoEventDatasets, {
  SWOOGO_EVENT_DATASETS_QUERY_KEY,
} from "routes/meta_datasets/swoogo_events/useSwoogoEventDatasets";
import MetaDatasetService from "services/MetaDataset";

const EMPTY = [];
const STANDALONE_GROUP = "Standalone Meta Datasets";
// base-ui Select cannot store "no selection" and "cleared" as the same value, so
// a sentinel stands for the explicit "No dataset" choice and maps back to null.
const NONE_VALUE = "__none__";

// The backend orders rows by account name then dataset name, so inserting in
// encounter order keeps account groups stable with the standalone group last.
const buildGroups = datasets => {
  const byAccount = new Map();
  const standalone = [];
  datasets.forEach(dataset => {
    if (dataset.meta_ad_account_name) {
      if (!byAccount.has(dataset.meta_ad_account_name)) {
        byAccount.set(dataset.meta_ad_account_name, []);
      }
      byAccount.get(dataset.meta_ad_account_name).push(dataset);
    } else {
      standalone.push(dataset);
    }
  });
  const groups = [...byAccount.entries()].map(([label, items]) => ({
    label,
    items,
  }));
  if (standalone.length > 0) {
    groups.push({ label: STANDALONE_GROUP, items: standalone });
  }
  return groups;
};

// A removed dataset is not in the picker, so the dialog opens on its
// placeholder rather than on an id the picker cannot name.
const initialChoice = (currentDatasetId, datasets) => {
  if (currentDatasetId === null) {
    return NONE_VALUE;
  }
  const isSelectable = datasets.some(
    dataset => dataset.id === currentDatasetId
  );
  return isSelectable ? currentDatasetId : null;
};

/**
 * Controlled modal that links one Meta dataset to the given events, or clears
 * the link via the "No dataset" option. A single event opens on its current
 * dataset. Nothing is sent to Meta.
 */
const EventDatasetDialog = ({ eventIds, open, onOpenChange, onSuccess }) => {
  const { enable_leads_feature } = useRuvixxFlags();
  const queryClient = useQueryClient();
  const isSingle = eventIds.length === 1;
  const [choice, setChoice] = useState(null);

  const datasetsQuery = useQuery({
    queryKey: ["meta-selectable-datasets"],
    queryFn: () => MetaDatasetService.listSelectable(),
    enabled: open && !!enable_leads_feature,
  });
  const datasets = datasetsQuery.data ?? EMPTY;

  const { links, isLoaded: areLinksLoaded } = useSwoogoEventDatasets(eventIds, {
    enabled: open && isSingle && !!enable_leads_feature,
  });
  const currentDatasetId = isSingle
    ? (links.get(eventIds[0])?.meta_dataset_id ?? null)
    : undefined;
  const isCurrentKnown = isSingle && areLinksLoaded && datasetsQuery.isSuccess;
  const selected =
    choice ??
    (isCurrentKnown ? initialChoice(currentDatasetId, datasets) : null);

  const saveMutation = useMutation({
    mutationFn: () =>
      MetaDatasetService.setSwoogoEventDataset(
        eventIds,
        selected === NONE_VALUE ? null : selected
      ),
    onSuccess: () => {
      toast.success("Dataset updated");
      queryClient.invalidateQueries({
        queryKey: [SWOOGO_EVENT_DATASETS_QUERY_KEY],
      });
      onOpenChange(false);
      onSuccess?.();
    },
    onError: err =>
      toast.error(formatError(err, "Could not update the dataset")),
  });

  const groups = buildGroups(datasets);
  const nameById = new Map(datasets.map(dataset => [dataset.id, dataset.name]));

  const resolvedChoice = selected === NONE_VALUE ? null : selected;
  const isUnchanged = isSingle && resolvedChoice === currentDatasetId;
  const isSaveDisabled =
    selected === null || isUnchanged || saveMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isSingle ? "Attach Dataset" : "Set Dataset"}
          </DialogTitle>
          {!isSingle && (
            <DialogDescription>
              This overwrites the dataset on {eventIds.length} selected events.
            </DialogDescription>
          )}
        </DialogHeader>
        <Select value={selected} onValueChange={setChoice}>
          <SelectTrigger className="tw:w-full">
            <SelectValue placeholder="Select a dataset">
              {value =>
                value === NONE_VALUE
                  ? "No dataset"
                  : (nameById.get(value) ?? value)
              }
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value={NONE_VALUE}>No dataset</SelectItem>
            </SelectGroup>
            {groups.map(group => (
              <SelectGroup key={group.label}>
                <SelectLabel>{group.label}</SelectLabel>
                {group.items.map(dataset => (
                  <SelectItem key={dataset.id} value={dataset.id}>
                    {dataset.name || dataset.dataset_id}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

EventDatasetDialog.propTypes = {
  eventIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default EventDatasetDialog;
